import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { AddCircle, RemoveCircle, UploadFile } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../api/useApi";
import Notification from "../../components/Notification";
import pako from 'pako';
import imageCompression from "browser-image-compression";

export const compressImageToBase64 = async (file) => {
  try {
    // Step 1: Resize + quality compression using browser-image-compression
    const compressedFile = await imageCompression(file, {
      maxSizeMB: 0.1,          // ~100KB target
      maxWidthOrHeight: 300,   // Resize to 300px max
      useWebWorker: true,
    });

    // Step 2: Convert to WebP (optional, more compression)
    const webpFile = await imageCompression(compressedFile, {
      fileType: "image/webp",
      maxSizeMB: 0.1,
      useWebWorker: true,
    });

    // Step 3: Convert file to Base64 string
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(webpFile);
      reader.onload = () => resolve(reader.result.split(",")[1]); // remove "data:image/webp;base64,"
      reader.onerror = reject;
    });

    // Step 4: Gzip compress the Base64
    const binary = atob(base64);
    const charData = binary.split("").map((c) => c.charCodeAt(0));
    const binData = new Uint8Array(charData);
    const compressed = pako.gzip(binData);

    // Step 5: Convert compressed binary to Base64 again
    const compressedBase64 = btoa(String.fromCharCode(...compressed));
    return compressedBase64;
  } catch (err) {
    console.error("Image compression failed:", err);
    throw err;
  }
};

// export const decompressBase64ToDataURL = (compressedBase64) => {
//   try {
//     // Step 1: Decode base64 -> binary string
//     const binary = atob(compressedBase64);

//     // Step 2: Convert binary string to Uint8Array
//     const charData = binary.split("").map((c) => c.charCodeAt(0));
//     const binData = new Uint8Array(charData);

//     // Step 3: Ungzip
//     const decompressed = pako.ungzip(binData);

//     // Step 4: Convert decompressed bytes back to binary string
//     const byteString = String.fromCharCode(...decompressed);

//     // Step 5: Encode to base64 again for browser
//     const base64 = btoa(byteString);

//     // Step 6: Return data URL (assuming WebP)
//     return `data:image/webp;base64,${base64}`;
//   } catch (err) {
//     console.error("Failed to decompress image:", err);
//     return null;
//   }
// };

// === Default SVG Icon ===
export const decompressBase64ToDataURL = (compressedBase64) => {
  try {
    if (!compressedBase64) return null;

    // Remove unwanted quotes or data URL prefix if present
    compressedBase64 = compressedBase64.trim().replace(/^"(.*)"$/, "$1");

    // Step 1: Decode base64 -> binary
    const binary = atob(compressedBase64);

    // Step 2: Convert string to bytes
    const charData = binary.split("").map(c => c.charCodeAt(0));
    const binData = new Uint8Array(charData);

    // Step 3: GZIP decompress
    const decompressed = pako.ungzip(binData);

    // Step 4: Convert bytes -> base64
    const byteString = String.fromCharCode(...decompressed);
    const finalBase64 = btoa(byteString);

    return `data:image/webp;base64,${finalBase64}`;
  } catch (e) {
    console.error("❌ Icon decompress failed", e);
    return null;
  }
};


const AddNewDomainIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 512 512">
    <defs>
      <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#b5c4ff" />
        <stop offset="100%" stopColor="#dfe3ff" />
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="8" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    <circle cx="256" cy="256" r="180" fill="url(#grad)" filter="url(#glow)" />
    <path
      d="M256 176v160M176 256h160"
      stroke="#1a1a1a"
      strokeWidth="24"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DefaultDomainIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 512 512">
    <defs>
      <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffa07a" />
        <stop offset="100%" stopColor="#ff6347" />
      </linearGradient>
      <filter id="glow2">
        <feGaussianBlur stdDeviation="6" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    <polygon
      points="256,80 416,160 416,352 256,432 96,352 96,160"
      fill="url(#grad2)"
      filter="url(#glow2)"
    />
    <circle cx="256" cy="256" r="60" fill="rgba(255,255,255,0.6)" />
  </svg>
);

const SelectDomain = () => {
  const navigate = useNavigate();
  const { request } = useApi();
  const [open, setOpen] = useState(false);
  const [domainName, setDomainName] = useState("");
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [categories, setCategories] = useState([{ categoryName: "", weightage: "" }]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notif, setNotif] = useState({ open: false, success: false, text: "" });

  // === Fetch domains ===
  // useEffect(() => {
  //   const fetchDomains = async () => {
  //     try {
  //       const data = await request({ url: `/domains-with-categories` });
  //       setDomains(data);
  //     } catch (err) {
  //       console.error(err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  //   fetchDomains();
  // }, [request]);

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const data = await request({ url: `/domains-with-categories` });
  
        // Mark domains that are empty (no categories)
        const domainsWithStatus = data.map((d) => {
          const hasCategories = d.categories?.length > 0;
          return {
            ...d,
            isDisabled: !hasCategories, // ✅ Enable if at least one category exists
          };
        });
  
        setDomains(domainsWithStatus);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchDomains();
  }, [request]);
  

  const handleDomain = (domain) => {
    if (domain === "Add New") setOpen(true);
    else navigate(`/admin/${domain.toLowerCase()}/admin-landingpage`);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setIconFile(file);
    setIconPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleCreate = async () => {
    if (!domainName.trim() || categories.length === 0 || !iconFile) {
      setNotif({ open: true, success: false, text: "All fields are mandatory." });
      return;
    }

    const validCategories = categories.filter((c) => c.categoryName.trim() !== "");
    if (validCategories.length === 0) {
      setNotif({ open: true, success: false, text: "Please add at least one category." });
      return;
    }

    const compressedBase64 = await compressImageToBase64(iconFile)

    try {
      const payload = {
        domainName: domainName.trim(),
        icon: compressedBase64,
        categories: validCategories.map((c) => ({
          name: c.categoryName.trim(),
          weightage: Number(c.weightage) || 0,
        })),
      };

      await request({
        url: `/domain-with-categories`,
        method: "POST",
        data: payload,
        headers: { "Content-Type": "application/json" },
      });

      setNotif({ open: true, success: true, text: "Domain created successfully!" });
      setOpen(false);
      setDomainName("");
      setIconFile(null);
      setIconPreview(null);
      setCategories([{ categoryName: "", weightage: "" }]);

      // Refresh list
      const updated = await request({ url: `/domains-with-categories` });
      setDomains(updated);
    } catch (err) {
      console.error(err);
      setNotif({ open: true, success: false, text: "Failed to create domain." });
    }
  };

  const handleCategoryChange = (i, field, value) => {
    const updated = [...categories];
    updated[i][field] = value;
    setCategories(updated);
  };

  const addCategory = () => setCategories([...categories, { categoryName: "", weightage: "" }]);
  const removeCategory = (i) => {
    if (categories.length > 1) setCategories(categories.filter((_, idx) => idx !== i));
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "80vh", 
        background: "radial-gradient(circle at center, #1b1f2f, #0e0f1a)",
      }}
    >
     <Paper
  elevation={10}
  sx={{
    p: 2,
    borderRadius: "28px",
    maxWidth: "77vw",
    width: "77vw",
    background: "rgba(35,40,60,0.95)",
    textAlign: "center",
    backdropFilter: "blur(14px)",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 0 15px rgba(0,0,0,0.5)",
  }}
>
  <Typography variant="h5" sx={{ color: "#fff", mb: 2, fontWeight: 600 }}>
    Select the domain you’d like to view or edit.
  </Typography>
  {/* <Typography variant="body1" sx={{ color: "#bbb", mb: 6 }}>
    Click Add New to create a new domain and assign a role.
  </Typography> */}

  {loading ? (
    <CircularProgress sx={{ color: "#fff" }} />
  ) : (
    <Grid container spacing={6} justifyContent="center">
      {/* Add New */}
      {/* <Grid item key="Add New">
        <Button
          onClick={() => handleDomain("Add New")}
          sx={{
            backgroundColor: "#5a5a6a",
            width: 180,
            height: 180,
            borderRadius: 6,
            flexDirection: "column",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            "&:hover": { transform: "scale(1.07)", transition: "0.25s" },
          }}
        >
          <AddNewDomainIcon />
          <Typography sx={{ mt: 2, fontSize: 18, fontWeight: 500 }}>
            Add New
          </Typography>
        </Button>
      </Grid> */}

      {/* Existing Domains */}
      {/* Admin Dashboard Card */}
      <Grid item>
        <Button
          onClick={() => navigate(`/admin/admin-dashboard`)}
          sx={{
           backgroundColor:  "rgba(224,230,255,0.9)",
            width: 180,
            height: 180,
            borderRadius: 6,
            flexDirection: "column",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#000",
            boxShadow: "none",
             "&:hover": {
                transform: "scale(1.07)",
                transition: "0.25s",
                backgroundColor:"rgba(220,230,255,1)",
              },
          }}
        >
          <Box
            sx={{
              width: 90,
              height: 90,
              borderRadius: "50%",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 42,
              fontWeight: "bold",
            }}
          >
            📊
          </Box>

          <Typography
            sx={{
              mt: 2,
              fontSize: 18,
            }}
          >
            Admin Dashboard
          </Typography>
        </Button>
      </Grid>
      {domains.map((d) => (
        <Grid item key={d.id}>
          <Button
            onClick={() => !d.isDisabled && handleDomain(d.name)}
            disabled={d.isDisabled}
            sx={{
              backgroundColor: d.isDisabled
                ? "rgba(150,150,150,0.25)"
                : "rgba(224,230,255,0.9)",
              width: 180,
              height: 180,
              borderRadius: 6,
              flexDirection: "column",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: d.isDisabled ? "#777" : "#000",
              boxShadow: "none",
              overflow: "visible",
              opacity: d.isDisabled ? 0.6 : 1,
              pointerEvents: d.isDisabled ? "none" : "auto",
              "&:hover": {
                transform: d.isDisabled ? "none" : "scale(1.07)",
                transition: "0.25s",
                backgroundColor: d.isDisabled
                  ? "rgba(150,150,150,0.25)"
                  : "rgba(220,230,255,1)",
              },
            }}
          >
            {d.icon ? (
              <img
                src={decompressBase64ToDataURL(d.icon)}
                alt={d.name}
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "12px",
                  filter: d.isDisabled ? "grayscale(100%)" : "none",
                }}
              />
            ) : (
              <DefaultDomainIcon />
            )}

            <Typography
              sx={{
                mt: 2,
                fontSize: 18,
                fontWeight: 500,
                color: d.isDisabled ? "#888" : "#000",
              }}
            >
              {d.name}
            </Typography>

            {d.isDisabled && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#aaa",
                  mt: 0.5,
                  fontStyle: "italic",
                }}
              >
                No assessments available
              </Typography>
            )}
          </Button>
        </Grid>
      ))}
    </Grid>
  )}
</Paper>


      {/* === Dialog === */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: "rgba(245,245,245,0.85)",
            backdropFilter: "blur(12px)",
            color: "#222",
            boxShadow: "0 4px 30px rgba(0,0,0,0.3)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#111" }}>Create New Domain</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Domain Name *"
            value={domainName}
            onChange={(e) => setDomainName(e.target.value)}
            margin="normal"
            variant="outlined"
          />

          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFile />}
              sx={{
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              {iconFile ? "Change Icon" : "Upload Icon *"}
              <input type="file" hidden accept="image/*" onChange={handleFileChange} />
            </Button>

            {iconPreview && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={iconPreview}
                  alt="icon preview"
                  style={{ width: 100, height: 100, borderRadius: "12px" }}
                />
              </Box>
            )}
          </Box>

          <Typography sx={{ mt: 3, mb: 1, fontWeight: 500 }}>Categories *</Typography>
          {categories.map((cat, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                label={`Category ${index + 1}`}
                value={cat.categoryName}
                onChange={(e) => handleCategoryChange(index, "categoryName", e.target.value)}
                variant="outlined"
              />
              <TextField
                label="Weightage"
                type="number"
                value={cat.weightage}
                onChange={(e) => handleCategoryChange(index, "weightage", e.target.value)}
                sx={{ width: 120 }}
                variant="outlined"
              />
              <IconButton
                sx={{ color: "#e53935" }}
                onClick={() => removeCategory(index)}
                disabled={categories.length === 1}
              >
                <RemoveCircle />
              </IconButton>
            </Box>
          ))}

          <Button
            startIcon={<AddCircle />}
            sx={{ mt: 1, color: "#1a73e8", textTransform: "none", fontWeight: 500 }}
            onClick={addCategory}
          >
            Add Category
          </Button>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: "#444" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#5a7bff", "&:hover": { backgroundColor: "#506de5" } }}
            onClick={handleCreate}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Notification
        open={notif.open}
        success={notif.success}
        text={notif.text}
        onClose={() => setNotif({ ...notif, open: false })}
      />
    </Box>
  );


};

export default SelectDomain;